import { useState, useEffect } from "react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import { ActivityType } from "../../interface/ActivityType";
import { Option2 } from "../../interface/ActivityType";
import { ActivityTypeAxiosInstance } from "../../Network/ActivityTypeAxiosInstance";

interface ActivitySelectorProps {
  onSelectActivity: (selectedActivity: string) => void;
  selectedActivityType?: string;
}

const LABEL_MAP: Record<string, string> = {
  cpt: "CPT",
  dental: "Dental",
  hcpcs: "HCPCS",
  drugs: "Drugs",
};

const formatLabel = (value: string) => {
  const key = value?.toLowerCase?.() ?? "";
  return LABEL_MAP[key] ?? (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);
};

const ActivitySelector: React.FC<ActivitySelectorProps> = ({
  onSelectActivity,
  selectedActivityType,
}) => {
  const [options, setOptions] = useState<Option2[]>([]);
  const [selectedOption, setSelectedOption] = useState<string | undefined>(
    selectedActivityType || undefined
  );

  // Fetch activities from API
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const response = await ActivityTypeAxiosInstance.get(
          "getactivity/activitytype"
        );
        if (response.data.success) {
          const activities: ActivityType[] = response.data.activities;
          const newOptions = activities.map((activity) => {
            const val = (activity.activity_type ?? "").toString();
            return {
              value: val,
              label: formatLabel(val),
            } as Option2;
          });
          setOptions(newOptions);
        } else {
          console.error("Failed to fetch activities");
        }
      } catch (error) {
        console.error("Error fetching activities:", error);
      }
    };

    fetchActivities();
  }, []);

  useEffect(() => {
    if (selectedActivityType) {
      setSelectedOption(selectedActivityType);
    } else {
      setSelectedOption(undefined);
    }
  }, [selectedActivityType]);

  const handleChange = (value: string) => {
    setSelectedOption(value);
    onSelectActivity(value);
  };

  return (
    <Select onValueChange={(value) => handleChange(value)} value={selectedOption}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Select an activity type..." />
      </SelectTrigger>
      <SelectContent>
        {options.length > 0 ? (
          options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))
        ) : (
          <div className="p-4 text-gray-500">No options available</div>
        )}
      </SelectContent>
    </Select>
  );
};

export default ActivitySelector;
